import React, { useState, useEffect, useMemo } from 'react';

const PLATFORM_COLORS = { amazon: '#FF9900', ebay: '#E53238', walmart: '#0071CE', aip: '#8B5CF6', general: '#10b981' };
const getPlatformColor = (p) => PLATFORM_COLORS[p] || '#6b7280';
const getPlatformLabel = (p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : '');

const extractImageUrl = (raw) => {
    if (!raw) return '';
    const s = String(raw);
    const match = s.match(/=IMAGE\("([^"]+)"\)/i);
    if (match) return match[1];
    if (s.startsWith('http') || s.startsWith('data:')) return s;
    return '';
};

function ProductCard({ row, headers, rowIndex, onDelete, onEdit, platform }) {
    const get = (label) => {
        const idx = headers.indexOf(label);
        return idx !== -1 ? row[idx] : '';
    };
    const findByPartial = (partials) => {
        for (const p of partials) {
            const key = headers.find(h => h.toLowerCase().includes(p.toLowerCase()));
            if (key) {
                const idx = headers.indexOf(key);
                if (idx !== -1 && row[idx]) return { label: key, value: row[idx] };
            }
        }
        return null;
    };

    const imageHeader = headers.find(h => h.toLowerCase().includes('image'));
    const rawImage = imageHeader ? row[headers.indexOf(imageHeader)] : '';
    const imageUrl = extractImageUrl(rawImage);

    const nameHeader = headers.find(h => h.toLowerCase() === 'name' || h.toLowerCase() === 'title' || h.toLowerCase().includes('name') || h.toLowerCase().includes('title'));
    const name = nameHeader ? row[headers.indexOf(nameHeader)] : '';

    const urlHeader = headers.find(h => h.toLowerCase().includes('url'));
    const url = urlHeader ? row[headers.indexOf(urlHeader)] : '';

    const profit = findByPartial(['net profit', 'profit ($)']);
    const roi = findByPartial(['roi']);
    const cost = findByPartial(['cost price', 'buy cost']);
    const sale = findByPartial(['sale price']);
    const qty = findByPartial(['quantity', 'qty']);
    const asin = findByPartial(['asin']);
    const upc = findByPartial(['upc']);
    const date = findByPartial(['date']);
    const notes = findByPartial(['notes']);
    const supplier = findByPartial(['supplier', 'source']);

    const profitNum = profit ? parseFloat(profit.value) : null;
    const roiNum = roi ? parseFloat(roi.value) : null;
    const platformColor = getPlatformColor(platform);

    return (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ height: '150px', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', borderBottom: '1px solid #f3f4f6' }}>
                {imageUrl ? (
                    <img src={imageUrl} alt={name} style={{ maxWidth: '100%', maxHeight: '130px', objectFit: 'contain', padding: '8px' }}
                        onError={e => { e.target.style.display = 'none'; }} />
                ) : (
                    <div style={{ fontSize: '36px', color: '#d1d5db' }}>📦</div>
                )}
                <div style={{ position: 'absolute', top: '8px', left: '8px', background: platformColor, color: 'white', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', textTransform: 'uppercase' }}>
                    {getPlatformLabel(platform)}
                </div>
                {date && (
                    <div style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '6px' }}>
                        {date.value}
                    </div>
                )}
            </div>

            <div style={{ padding: '12px', flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#111827', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '36px' }} title={name}>
                    {name || '(No title)'}
                </p>

                {(asin || upc) && (
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {asin && <span style={{ fontSize: '10px', background: '#eff6ff', color: '#1d4ed8', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>{asin.value}</span>}
                        {upc && <span style={{ fontSize: '10px', background: '#f0fdf4', color: '#15803d', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>UPC: {upc.value}</span>}
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    {cost && <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '6px 8px' }}><div style={{ fontSize: '10px', color: '#6b7280' }}>Cost</div><div style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>${cost.value}</div></div>}
                    {sale && <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '6px 8px' }}><div style={{ fontSize: '10px', color: '#6b7280' }}>Sale</div><div style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>${sale.value}</div></div>}
                    {profit && <div style={{ background: profitNum > 0 ? '#f0fdf4' : '#fef2f2', borderRadius: '6px', padding: '6px 8px' }}><div style={{ fontSize: '10px', color: '#6b7280' }}>Profit</div><div style={{ fontSize: '13px', fontWeight: '600', color: profitNum > 0 ? '#15803d' : '#dc2626' }}>${Number(profit.value || 0).toFixed(2)}</div></div>}
                    {roi && <div style={{ background: roiNum > 0 ? '#f0fdf4' : '#fef2f2', borderRadius: '6px', padding: '6px 8px' }}><div style={{ fontSize: '10px', color: '#6b7280' }}>ROI</div><div style={{ fontSize: '13px', fontWeight: '600', color: roiNum > 0 ? '#15803d' : '#dc2626' }}>{Number(roi.value || 0).toFixed(2)}%</div></div>}
                    {qty && <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '6px 8px' }}><div style={{ fontSize: '10px', color: '#6b7280' }}>Qty</div><div style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{qty.value}</div></div>}
                    {supplier && <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '6px 8px' }}><div style={{ fontSize: '10px', color: '#6b7280' }}>Source</div><div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={supplier.value}>{supplier.value}</div></div>}
                </div>

                {notes && notes.value && (
                    <p style={{ margin: 0, fontSize: '11px', color: '#6b7280', fontStyle: 'italic', background: '#fffbeb', padding: '4px 8px', borderRadius: '4px', borderLeft: '2px solid #fbbf24', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={notes.value}>
                        {notes.value}
                    </p>
                )}
            </div>

            <div style={{ padding: '10px 12px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '6px' }}>
                {url && (
                    <a href={url} target="_blank" rel="noreferrer" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', background: platformColor, color: 'white', borderRadius: '6px', fontSize: '11px', fontWeight: '600', textDecoration: 'none' }}>
                        🔗 View Listing
                    </a>
                )}
                <button onClick={() => onEdit(rowIndex)} style={{ padding: '6px 10px', background: '#eff6ff', color: '#1d4ed8', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }} title="Edit">✏️</button>
                <button onClick={() => onDelete(rowIndex)} style={{ padding: '6px 10px', background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }} title="Delete">🗑️</button>
            </div>
        </div>
    );
}

export default function BuylistViewerModal({ isOpen, onClose, authService }) {
    const [allLists, setAllLists] = useState([]);
    const [currentListName, setCurrentListName] = useState('');
    const [currentPlatform, setCurrentPlatform] = useState('');
    const [viewData, setViewData] = useState([]);
    const [viewItemIds, setViewItemIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('date');
    const [sortDir, setSortDir] = useState('asc');
    const [showListSwitcher, setShowListSwitcher] = useState(false);
    const [editingRowIndex, setEditingRowIndex] = useState(null);
    const [editingRowData, setEditingRowData] = useState({});
    const [deleteListConfirm, setDeleteListConfirm] = useState(false);
    const [message, setMessage] = useState(null);

    const showMsg = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 3000);
    };

    useEffect(() => {
        if (!isOpen) {
            setSearch('');
            setDeleteListConfirm(false);
            setShowListSwitcher(false);
            return;
        }
        const init = async () => {
            setLoading(true);
            const result = await authService.getBuyListNames('all');
            if (result.success && result.listNames.length > 0) {
                setAllLists(result.listNames);
                const first = result.listNames[0];
                setCurrentListName(first.name);
                setCurrentPlatform(first.platform);
                await loadData(first.name, first.platform);
            } else {
                setLoading(false);
            }
        };
        init();
    }, [isOpen]);

    const loadData = async (name, platform) => {
        setLoading(true);
        const result = await authService.exportBuyList(name, platform);
        if (result.success) {
            setViewData(result.csvData);
            setViewItemIds(result.item_ids || []);
        } else {
            showMsg(result.error, 'error');
        }
        setLoading(false);
    };

    const switchList = async (name, platform) => {
        setCurrentListName(name);
        setCurrentPlatform(platform);
        setShowListSwitcher(false);
        setViewData([]);
        setViewItemIds([]);
        await loadData(name, platform);
    };

    const headers = viewData.length > 0 ? viewData[0] : [];
    const dataRows = viewData.length > 1 ? viewData.slice(1) : [];

    const filteredRows = useMemo(() => {
        let rows = dataRows.map((row, i) => ({ row, originalIndex: i }));
        if (search.trim()) {
            const q = search.toLowerCase();
            rows = rows.filter(({ row }) => row.some(cell => String(cell).toLowerCase().includes(q)));
        }
        if (sortBy === 'profit' || sortBy === 'roi') {
            const key = sortBy === 'profit' ? 'net profit' : 'roi';
            const idx = headers.findIndex(h => h.toLowerCase().includes(key));
            if (idx !== -1) {
                rows.sort((a, b) => {
                    const av = parseFloat(a.row[idx]) || 0;
                    const bv = parseFloat(b.row[idx]) || 0;
                    return sortDir === 'desc' ? bv - av : av - bv;
                });
            }
        } else if (sortDir === 'asc') {
            rows.reverse();
        }
        return rows;
    }, [dataRows, search, sortBy, sortDir, headers]);

    const handleDeleteRow = async (originalIndex) => {
        if (!window.confirm('Delete this item from the list?')) return;
        const itemId = viewItemIds[originalIndex];
        const result = await authService.deleteBuyListItem(itemId);
        if (result.success) {
            setViewData(prev => prev.filter((_, i) => i !== originalIndex + 1));
            setViewItemIds(prev => prev.filter((_, i) => i !== originalIndex));
            showMsg('Item deleted');
        } else {
            showMsg('Failed to delete item', 'error');
        }
    };

    const handleEditRow = (originalIndex) => {
        const rowObj = {};
        headers.forEach((h, i) => { rowObj[h] = dataRows[originalIndex][i] || ''; });
        setEditingRowIndex(originalIndex);
        setEditingRowData(rowObj);
    };

    const handleSaveEdit = async () => {
        const itemId = viewItemIds[editingRowIndex];
        const result = await authService.updateBuyListItem(itemId, editingRowData);
        if (result.success) {
            const newRow = headers.map(h => editingRowData[h] || '');
            setViewData(prev => {
                const updated = [...prev];
                updated[editingRowIndex + 1] = newRow;
                return updated;
            });
            setEditingRowIndex(null);
            showMsg('Changes saved');
        } else {
            showMsg('Failed to save changes', 'error');
        }
    };

    const handleExport = () => {
        const csvContent = viewData
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `buy_list_${currentListName}_${currentPlatform}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        showMsg(`Exported ${dataRows.length} items`);
    };

    const handleDeleteList = async () => {
        if (!deleteListConfirm) { setDeleteListConfirm(true); return; }
        const result = await authService.deleteBuyList(currentListName, currentPlatform);
        if (result.success) {
            showMsg('List deleted');
            const remaining = allLists.filter(l => !(l.name === currentListName && l.platform === currentPlatform));
            setAllLists(remaining);
            setDeleteListConfirm(false);
            if (remaining.length > 0) {
                await switchList(remaining[0].name, remaining[0].platform);
            } else {
                onClose();
            }
        } else {
            showMsg(result.error || 'Failed to delete list', 'error');
            setDeleteListConfirm(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 999999, display: 'flex', flexDirection: 'column' }} onClick={onClose}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f3f4f6', margin: '16px', marginTop: "10vh", borderRadius: '16px', overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.4)' }} onClick={e => e.stopPropagation()}>

                <div style={{ background: 'white', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                            <button onClick={() => setShowListSwitcher(p => !p)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', minWidth: 0 }}>
                                <span style={{ fontSize: '16px', fontWeight: '700', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {currentListName || 'No list selected'}
                                </span>
                                <span style={{ fontSize: '20px', color: 'black' }}>▾ <span
                                    style={{
                                        alignItems: 'center',
                                        gap: '6px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: '#15803d',
                                        background: '#f0fdf4',
                                        border: '1px solid #bbf7d0',
                                        borderRadius: '8px',
                                        padding: '6px 12px',
                                        cursor: 'pointer',
                                        transition: 'background 0.15s, border-color 0.15s',
                                        flexShrink: 0,
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#dcfce7'; e.currentTarget.style.borderColor = '#86efac'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#bbf7d0'; }}
                                >
                                    🔀 Switch Buylist
                                </span></span>
                            </button>
                            {showListSwitcher && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)', zIndex: 100, minWidth: '200px' }}>
                                    {allLists.length === 0 && <div style={{ padding: '10px 14px', fontSize: '13px', color: '#9ca3af' }}>No lists found</div>}
                                    {allLists.map(item => (
                                        <div key={`${item.name}__${item.platform}`} onClick={() => switchList(item.name, item.platform)}
                                            style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: item.name === currentListName && item.platform === currentPlatform ? '700' : '400', color: item.name === currentListName && item.platform === currentPlatform ? getPlatformColor(item.platform) : '#374151', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>{item.name === currentListName && item.platform === currentPlatform ? '✓ ' : ''}{item.name}</span>
                                            <span style={{ fontSize: '10px', fontWeight: '700', color: 'white', background: getPlatformColor(item.platform), padding: '1px 6px', borderRadius: '10px', textTransform: 'uppercase' }}>{item.platform}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {currentPlatform && (
                            <span style={{ background: getPlatformColor(currentPlatform), color: 'white', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', textTransform: 'uppercase', flexShrink: 0 }}>
                                {getPlatformLabel(currentPlatform)}
                            </span>
                        )}
                        <span style={{ fontSize: '12px', color: '#6b7280', flexShrink: 0 }}>{dataRows.length} item{dataRows.length !== 1 ? 's' : ''}</span>
                        <button onClick={onClose} style={{ padding: '6px 10px', background: 'none', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', color: '#6b7280', flexShrink: 0 }}>✕</button>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: '1 1 140px', minWidth: '120px' }}>
                            <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: '#9ca3af' }}>🔍</span>
                            <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
                                style={{ width: '100%', paddingLeft: '28px', paddingRight: '8px', paddingTop: '7px', paddingBottom: '7px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <select value={`${sortBy}_${sortDir}`} onChange={e => { const [f, d] = e.target.value.split('_'); setSortBy(f); setSortDir(d); }}
                            style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', color: '#374151', background: 'white', cursor: 'pointer' }}>
                            <option value="date_asc">Date Added ↑</option>
                            <option value="date_desc">Date Added ↓</option>
                            <option value="profit_desc">Profit ↓</option>
                            <option value="profit_asc">Profit ↑</option>
                            <option value="roi_desc">ROI ↓</option>
                            <option value="roi_asc">ROI ↑</option>
                        </select>
                        <button onClick={handleExport} style={{ padding: '7px 14px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>📥 Export</button>
                        <button onClick={handleDeleteList} style={{ padding: '7px 14px', background: deleteListConfirm ? '#dc2626' : '#fee2e2', color: deleteListConfirm ? 'white' : '#dc2626', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                            {deleteListConfirm ? '⚠️ Confirm' : '🗑️ Delete List'}
                        </button>
                        {deleteListConfirm && (
                            <button onClick={() => setDeleteListConfirm(false)} style={{ padding: '7px 12px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                        )}
                    </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '80px 0', color: '#9ca3af' }}>Loading…</div>
                    ) : filteredRows.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '80px 0', color: '#9ca3af' }}>
                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#6b7280' }}>
                                {allLists.length === 0 ? 'No buy lists found' : (search ? 'No results found' : 'This list is empty')}
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' }}>
                            {filteredRows.map(({ row, originalIndex }) => (
                                <ProductCard key={originalIndex} row={row} headers={headers} rowIndex={originalIndex}
                                    onDelete={handleDeleteRow} onEdit={handleEditRow} platform={currentPlatform} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {editingRowIndex !== null && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000001 }} onClick={() => setEditingRowIndex(null)}>
                    <div style={{ background: 'white', borderRadius: '12px', padding: '24px', width: '500px', maxHeight: '80vh', overflowY: 'auto', marginTop: "7vh" }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>Edit Item</h3>
                        {Object.entries(editingRowData).map(([key, val]) => (
                            <div key={key} style={{ marginBottom: '12px' }}>
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>{key}</label>
                                <input value={val} onChange={e => setEditingRowData(prev => ({ ...prev, [key]: e.target.value }))}
                                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                            </div>
                        ))}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button onClick={() => setEditingRowIndex(null)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', background: 'white', fontSize: '13px' }}>Cancel</button>
                            <button onClick={handleSaveEdit} style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            {message && (
                <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', padding: '12px 24px', borderRadius: '8px', zIndex: 1000002, background: message.type === 'error' ? '#dc2626' : '#10b981', color: 'white', fontWeight: '600', fontSize: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                    {message.text}
                </div>
            )}
        </div>
    );
}